import { getCsrfToken } from "@/lib/csrf"

export type ShotPresetSlotKind = "character" | "location" | "prop"
export type ShotPresetAssetMode = "omit" | "hidden" | "visible"
export type ShotPresetEntityMode = "slot" | "internal"

export type ShotPresetDraftEntity = {
  kind: ShotPresetSlotKind
  position: number
  mode: ShotPresetEntityMode
  required: boolean
  label: string | null
  source_character_id?: number | null
  source_outfit_id?: number | null
  source_location_id?: number | null
  source_location_variant_id?: number | null
  source_prop_id?: number | null
  source_prop_state_id?: number | null
  thumb_url?: string | null
}

export type ShotPresetDraftAsset = {
  type: "reference_input" | "media_version"
  id: number
  kind: string
  position?: number
  label: string
  media?: string
  thumb_url?: string | null
  download_url?: string | null
  character_name?: string | null
  active?: boolean
  mandatory?: boolean
}

export type ShotPresetGenerationRecipe = {
  storyboard_image_provider?: string | null
  effective_video_generation_provider?: string | null
  generation_model_overrides?: {
    storyboard?: string | null
    shot_video?: string | null
  }
  duration_target_sec?: number | null
  frame_format?: string | null
}

export type ShotPresetDraft = {
  shot_id: number
  default_name: string
  cover_url: string | null
  entities: ShotPresetDraftEntity[]
  assets: ShotPresetDraftAsset[]
  asset_defaults: {
    reference_inputs?: Record<string, ShotPresetAssetMode>
    media_versions?: Record<string, ShotPresetAssetMode>
  }
  generation_recipe?: ShotPresetGenerationRecipe
}

export type ShotPresetSlotRow = {
  id: number
  kind: ShotPresetSlotKind
  position: number
  required: boolean
  label: string
  source_character_id?: number | null
  source_outfit_id?: number | null
  source_location_id?: number | null
  source_location_variant_id?: number | null
  source_prop_id?: number | null
  source_prop_state_id?: number | null
  mode?: "slot"
  thumb_url?: string | null
  download_url?: string | null
  subtitle?: string | null
  front_view_chrome?: boolean
}

export type ShotPresetAssetRow = {
  type: "reference_input" | "media_version"
  id: number
  kind: string
  label?: string
  consumer_hidden: boolean
  thumb_url?: string | null
  download_url?: string | null
  video?: boolean
  audio?: boolean
}

export type ShotPresetCard = {
  id: number
  name: string
  description: string | null
  status: string
  category?: string | null
  published_at?: string | null
  published?: boolean
  slot_count: number
  cover_url: string | null
  preview_video_url?: string | null
  source_shot_id: number | null
  created_at: string
}

export type ShotPresetPreviewShot = {
  id: number
  storyboard_preview_url?: string | null
  video_clip_url?: string | null
  shooting_board_preview_url?: string | null
  user_visible_prompt_summary?: string | null
}

export type ShotPresetCharacterOption = {
  id: number
  name: string
  reference_sheet_url?: string | null
  portrait_thumb_url?: string | null
  outfits: { id: number; name: string; reference_url?: string | null }[]
}

export type ShotPresetLocationOption = {
  id: number
  name: string
  reference_sheet_url?: string | null
  variants: { id: number; name: string; display_name: string; reference_url?: string | null }[]
}

export type ShotPresetPropOption = {
  id: number
  name: string
  thumb_url?: string | null
  reference_sheet_url?: string | null
  states: { id: number; name: string; reference_url?: string | null }[]
}

export type ShotPresetReferenceImage = {
  type: string
  name: string
  subtitle?: string | null
  url: string | null
  download_url?: string | null
  front_view_chrome?: boolean
}

export type ShotPresetDetail = ShotPresetCard & {
  generation_recipe: ShotPresetGenerationRecipe
  slots: ShotPresetSlotRow[]
  author_assets: ShotPresetAssetRow[]
  reference_images: ShotPresetReferenceImage[]
  consumer: {
    slots: ShotPresetSlotRow[]
    internal_characters: { id: number; name: string | null }[]
    internal_props: { id: number; name: string | null }[]
    internal_location: { id: number; name: string | null } | null
    visible_reference_inputs: {
      id: number
      kind: string
      label: string
      thumb_url?: string | null
      download_url?: string | null
    }[]
    visible_media_versions: { id: number; kind: string; thumb_url?: string | null; download_url?: string | null }[]
  }
  preview_shot: ShotPresetPreviewShot | null
  template_shot_id: number | null
  preview_shot_id: number | null
}

export type ShotPresetSlotFill = {
  slot_id: number
  character_id?: number
  outfit_id?: number | null
  location_id?: number
  location_variant_id?: number | null
  location_facing?: string | null
  prop_id?: number
  state_id?: number | null
}

type JsonOk = { ok?: boolean; error?: string; preset_id?: number; preview_shot_id?: number | null; message?: string }

async function requestJson<T>(url: string, init: RequestInit): Promise<T> {
  const res = await fetch(url, {
    credentials: "same-origin",
    ...init,
    headers: {
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "X-CSRF-Token": getCsrfToken(),
      ...(init.body instanceof FormData ? {} : { "Content-Type": "application/json" }),
      ...(init.headers ?? {}),
    },
  })
  const data = (await res.json().catch(() => ({}))) as T & JsonOk
  if (!res.ok || data.ok === false) {
    throw new Error(data.error ?? `HTTP ${res.status}`)
  }
  return data
}

export async function fetchShotPresetDraft(projectId: number, shotId: number): Promise<ShotPresetDraft> {
  const data = await requestJson<{ draft: ShotPresetDraft }>(
    `/projects/${projectId}/workspace/presets/capture_preview?shot_id=${shotId}`,
    { method: "GET" },
  )
  return data.draft
}

export async function createShotPreset(
  projectId: number,
  body: {
    shot_id: number
    name: string
    description?: string
    slot_configs: ShotPresetDraftEntity[]
    asset_decisions: ShotPresetDraft["asset_defaults"]
  },
): Promise<{ preset_id: number }> {
  return requestJson(`/projects/${projectId}/workspace/presets`, {
    method: "POST",
    body: JSON.stringify(body),
  })
}

export async function updateShotPreset(
  projectId: number,
  presetId: number,
  body: { name?: string; description?: string },
): Promise<{ preset_id: number }> {
  return requestJson(`/projects/${projectId}/workspace/presets/${presetId}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  })
}

export async function deleteShotPreset(projectId: number, presetId: number): Promise<void> {
  await requestJson(`/projects/${projectId}/workspace/presets/${presetId}`, { method: "DELETE" })
}

export async function applyShotPresetPreview(
  projectId: number,
  presetId: number,
  fills: ShotPresetSlotFill[],
): Promise<{ preview_shot_id: number | null }> {
  return requestJson(`/projects/${projectId}/workspace/presets/${presetId}/preview`, {
    method: "POST",
    body: JSON.stringify({ fills }),
  })
}

export async function refreshShotPreset(
  projectId: number,
  presetId: number,
  body: {
    name?: string
    description?: string
    slot_configs: ShotPresetDraftEntity[]
    asset_decisions: ShotPresetDraft["asset_defaults"]
  },
): Promise<{ preset_id: number }> {
  return requestJson(`/projects/${projectId}/workspace/presets/${presetId}/refresh`, {
    method: "POST",
    body: JSON.stringify(body),
  })
}

export async function publishShotPreset(
  projectId: number,
  presetId: number,
  category?: string,
): Promise<{ preset_id: number }> {
  return requestJson(`/projects/${projectId}/workspace/presets/${presetId}/publish`, {
    method: "POST",
    body: JSON.stringify({ category }),
  })
}

export async function unpublishShotPreset(projectId: number, presetId: number): Promise<{ preset_id: number }> {
  return requestJson(`/projects/${projectId}/workspace/presets/${presetId}/unpublish`, {
    method: "POST",
    body: JSON.stringify({}),
  })
}
